import { Ref, ref, computed } from '@vue/composition-api';
import { i18n } from './../../../../i18n';
import {
  AcoFormData,
  AdgroupFormData,
  AD_GROUP_WEBSITE_TYPES,
  CreativeConfig,
  FullNormalFormData,
  PixelInfo,
  PixelStatus,
} from '@tt4b/ttam-enums';
import { useAllowlist, useTrackingPixelListApi, useStore, useCreativeActionTypeIs } from '@tt4b/ttam-hooks';
import { createLogger } from '@tt4b/ttam-utils';
import { PixelItem } from './../../../adgroup/promotion-pixel/types';

const logger = createLogger('CreativePixelHooks');

export function useWebsiteEvents(opt: {
  copyNum: Ref<number>;
  adgroupData: Ref<AdgroupFormData>;
  adGroupPixelInfo: Ref<PixelInfo>;
  creativeConfig: Ref<CreativeConfig>;
  createMode: Ref<string>;
  creativeData: Ref<Partial<FullNormalFormData & AcoFormData>>;
  aadvid: string;
}) {
  const { adgroupData, adGroupPixelInfo, creativeConfig, createMode, creativeData, copyNum } = opt;

  const { state } = useStore();
  const aadvid = computed(() => state.baseInfo.value.aadvid);

  const useCommonPixelEventUrl = (option: { pixelCode: string; aadvid: string }) =>
    `/i18n/events_manager/pixel/detail/${option?.pixelCode}?aadvid=${option?.aadvid}`;

  const pixelPageUrl = computed(() => `/i18n/events_manager/pixel?aadvid=${opt?.aadvid}`);

  // ad group对应的pixel name
  const pixelLabel = computed(() => {
    const pixelId = adgroupData.value?.adRefPixelId;
    if (!pixelId) {
      return '-';
    }

    return adGroupPixelInfo.value?.name;
  });

  const isOffSitePixel = computed(() => adgroupData.value?.adRefPixelId && adGroupPixelInfo.value?.isOnSite === false);

  const disabled = computed(() => creativeConfig.value.disableAll);

  // pixel-list props
  const usePixelListDataSource = () => {
    const { isPcmPhase1White, isPcmPhase2White } = useAllowlist();
    const { isCreate, isEdit, isCopy } = useCreativeActionTypeIs({
      createMode,
      adgroupFormData: adgroupData,
    });

    const data = ref<PixelItem[]>([]);
    const isLoading = ref(false);
    const originalData = ref<PixelInfo[]>([]);
    const params = computed(() => ({
      promotionWebsiteType: AD_GROUP_WEBSITE_TYPES.offSite, // creative层级固定传0
      promotionTargetType: adgroupData.value?.promotionTargetType,
      objectiveType: adgroupData.value?.objectiveType,
    }));
    const activateActiveTips = (hasPCM: boolean) =>
      (!isPcmPhase1White.value && !isPcmPhase2White.value) ||
      ((isPcmPhase1White.value || isPcmPhase2White.value) && hasPCM);

    const activateInactiveExtraTips = (haasPCM: boolean) =>
      (isPcmPhase1White.value || isPcmPhase2White.value) && !haasPCM;

    const isEmpty = computed(() => data.value.length === 0);

    const isDisabled = (i: PixelInfo) => {
      if (isCreate.value) {
        return i.availableQuota < 1;
      } else if (isEdit.value) {
        return i.id !== creativeData.value?.trackingPixelId && i.availableQuota < 1;
      } else if (isCopy.value) {
        return i.availableQuota < copyNum.value;
      }

      return false;
    };

    const run = async () => {
      try {
        isLoading.value = true;
        const res = await useTrackingPixelListApi(params.value);
        originalData.value = res;

        data.value = res
          .filter(i => i.name !== '')
          .map(i => {
            const isActive = i.status === PixelStatus.ACTIVE;
            const item: PixelItem = {
              status: isActive ? 'active' : 'inactive',
              disabled: isDisabled(i),
              showStatusPopover: isActive ? activateActiveTips(i.hasPcmConfig) : false,
              title: i.name,
              desc: `ID：${i.pixelCode}`,
              id: i.id,
              disabledUrl: `/i18n/perf/campaign?aadvid=${aadvid.value}`,
            };

            if (i.status === PixelStatus.NO_RECENT_ACTIVITY) {
              item.tipsConfig = [
                {
                  desc: i18n.t('module_pixel_event_status_inactive_tooltip') as string,
                  name: i18n.t('module_pixel_status_tooltip_link') as string,
                  url: `${useCommonPixelEventUrl({
                    aadvid: aadvid.value,
                    pixelCode: i.pixelCode,
                  })}&type=test_events`,
                },
              ];
              if (activateInactiveExtraTips(i.hasPcmConfig)) {
                item.tipsConfigExtra = {
                  desc: i18n.t('module_pixel_wait_event_config') as string,
                  name: i18n.t('common_go_to_web_event_configuration') as string,
                  url: useCommonPixelEventUrl({
                    aadvid: aadvid.value,
                    pixelCode: i.pixelCode,
                  }),
                };
              }
            }

            return item;
          });

        isLoading.value = false;
      } catch (error) {
        isLoading.value = false;
        logger.error(error);
      }
    };

    return {
      run,
      data,
      isLoading,
      isEmpty,
      originalData,
    };
  };
  return {
    disabled,
    pixelLabel,
    pixelPageUrl,
    isOffSitePixel,

    usePixelListDataSource,
  };
}
